import { Box } from "@chakra-ui/layout";
import {
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import { SkeletonText } from "@chakra-ui/skeleton";
import { Table, Tbody, Td, Th, Thead, Tr } from "@chakra-ui/table";
import React, { memo } from "react";
import { FiClock, FiCopy, FiFilePlus } from "react-icons/fi";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { EditIcon, EllipsisIcon } from "../../styles/icons";
import DateUtil from "../../util/DateUtil";
import { CopyText } from "../Utilities/CopyText";

interface PassConfigurationTableProps {
  passConfigurations:
    | CherryHubAdminApi.CherryPassConfigurationSummaryItem[]
    | null;
  isLoading: boolean;
  debouncedQuery: string;
  onClickEditPassBuilder: (
    passConfiguration: CherryHubAdminApi.CherryPassConfigurationSummaryItem
  ) => void;
  onClickCopyPassConfiguration: (
    passConfiguration: CherryHubAdminApi.CherryPassConfigurationSummaryItem
  ) => void;
  onClickClonePassConfiguration: (
    passConfiguration: CherryHubAdminApi.CherryPassConfigurationSummaryItem
  ) => void;
  onClickViewUpdateHistory: (
    passConfiguration: CherryHubAdminApi.CherryPassConfigurationSummaryItem
  ) => void;
}

export const PassConfigurationTable = memo(
  ({
    passConfigurations,
    isLoading,
    debouncedQuery,
    onClickEditPassBuilder,
    onClickCopyPassConfiguration,
    onClickClonePassConfiguration,
    onClickViewUpdateHistory,
  }: PassConfigurationTableProps) => {
    return (
      <Box w="100%" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Pass Preview</Th>
              <Th>Pass Configuration Id</Th>
              <Th>Display Name</Th>
              <Th>Pass Description</Th>
              <Th>Created</Th>
              <Th>Last Modified</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {!isLoading &&
              (passConfigurations ?? []).map((row) => (
                <Tr key={row.Meta?.Id}>
                  <Td w="0.1%">
                    <Box h="100px" w="175px" mx="auto">
                      {row?.PassPreviewImageUrl && (
                        <Image
                          h="100%"
                          w="100%"
                          objectFit="contain"
                          src={row.PassPreviewImageUrl}
                          borderRadius="lg"
                          boxShadow="lg"
                        />
                      )}
                    </Box>
                  </Td>
                  <Td textAlign="center" w="0.1%" whiteSpace="nowrap">
                    <CopyText text={row.Meta?.Id!} hover adjustMargin>
                      {row.Meta?.Id}
                    </CopyText>
                  </Td>
                  <Td
                    w="0.1%"
                    whiteSpace="nowrap"
                    title="Edit pass configuration"
                    _hover={{ textDecoration: "underline", cursor: "pointer" }}
                    onClick={() => onClickEditPassBuilder(row)}
                  >
                    {row.DisplayName}
                  </Td>
                  <Td whiteSpace="nowrap">{row.PassDescription}</Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    {DateUtil.getLocalDateFormatFromString(
                      row.Meta?.DateCreated
                    )}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    {DateUtil.getLocalDateFormatFromString(
                      row.Meta?.DateUpdated
                    )}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        aria-label="Pass configuration options"
                        title="Options"
                        icon={<EllipsisIcon />}
                        variant="ghost"
                        size="sm"
                      />
                      <MenuList>
                        <MenuItem
                          icon={<EditIcon />}
                          onClick={() => onClickEditPassBuilder(row)}
                        >
                          Edit Pass Configuration
                        </MenuItem>
                        <MenuItem
                          icon={<FiCopy />}
                          onClick={() => onClickCopyPassConfiguration(row)}
                        >
                          Copy Configuration to Other Passes
                        </MenuItem>
                        <MenuItem
                          icon={<FiFilePlus />}
                          onClick={() => onClickClonePassConfiguration(row)}
                        >
                          Clone Pass Configuration
                        </MenuItem>
                        <MenuItem
                          icon={<FiClock />}
                          onClick={() => onClickViewUpdateHistory(row)}
                        >
                          View Update History
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              ))}
            {passConfigurations?.length === 0 && !isLoading && (
              <Tr>
                <Td colSpan={7} textAlign="center">
                  No pass configuration found matching query '{debouncedQuery}'
                </Td>
              </Tr>
            )}
            {isLoading && (
              <Tr>
                <Td width="10%">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="20%">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="25%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="25%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="10%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="10%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="0.1%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    );
  }
);
